import { Edit2, LinkIcon, ClipboardCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, type ReactNode } from 'react';
import { useProviderCheckoutDomainSettings } from '@/hooks/provider/useProviderCheckoutDomainSettings';
import { resolveCheckoutOrigin } from '@/lib/checkout-origin';

interface PricingPlan {
  id: string;
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  annualMonthlyPrice: number;
  annualDiscount: number;
  currency: string;
  features: string[];
  creditInfo?: string;
  // Team-specific fields
  type?: 'individual' | 'teams';
  includedSeats?: number;
  pricePerAdditionalSeat?: number;
  pricings?: Array<{ id?: string; billingCycleId?: string; billingCycleName?: string }>;
  productId?: string;
}

interface SubscriptionPricingCardProps {
  plan: PricingPlan;
  onEdit?: () => void;
  className?: string;
  headerActions?: ReactNode;
  badgeLabel?: string;
  badges?: Array<{
    label: string;
    variant?: 'default' | 'secondary' | 'destructive' | 'outline';
    className?: string;
  }>;
}

export function SubscriptionPricingCard({ 
  plan, 
  onEdit,
  className,
  headerActions,
  badgeLabel,
  badges
}: SubscriptionPricingCardProps) {
  const { t } = useTranslation();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { data: checkoutDomainSettings } = useProviderCheckoutDomainSettings();
  const checkoutOrigin = useMemo(
    () =>
      resolveCheckoutOrigin(
        checkoutDomainSettings?.checkoutDomain,
        checkoutDomainSettings?.checkoutDomainEnabled ?? false,
      ),
    [checkoutDomainSettings?.checkoutDomain, checkoutDomainSettings?.checkoutDomainEnabled],
  );
  
  const formatCurrency = (amount: number, currency: string = 'USD') => {
    const symbol = currency === 'USD' ? '$' : currency;
    return `${symbol}${amount}`;
  };

  const handleCopyLink = async (pricingId?: string, billingCycleId?: string) => {
    if (!plan.productId || !plan.id || !pricingId || !billingCycleId) {
      return;
    }

    const url = new URL('/customer/register', checkoutOrigin);
    url.searchParams.set('productId', plan.productId);
    url.searchParams.set('planId', plan.id);
    url.searchParams.set('cycleId', billingCycleId);
    url.searchParams.set('priceId', pricingId);

    const copyWithFallback = (text: string) => {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    };

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url.toString());
      } else {
        copyWithFallback(url.toString());
      }

      setCopiedId(pricingId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Copy checkout link failed', error);
    }
  };

  return (
    <Card className={cn(
      "bg-zinc-950 border-zinc-800 text-white",
      className
    )}>
      <CardHeader className="pb-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-white">{plan.name}</h3>
              {badges?.length
                ? badges.map((badge) => (
                    <Badge
                      key={badge.label}
                      variant={badge.variant ?? 'secondary'}
                      className={cn('text-[10px] uppercase tracking-wide', badge.className)}
                    >
                      {badge.label}
                    </Badge>
                  ))
                : badgeLabel
                  ? (
                      <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
                        {badgeLabel}
                      </Badge>
                    )
                  : null}
            </div>
            {plan.creditInfo && (
              <span className="text-sm text-zinc-400">{plan.creditInfo}</span>
            )}
          </div>
          {(headerActions || onEdit) && (
            <div className="flex items-center justify-end gap-2">
              {headerActions}
              {onEdit && (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={onEdit}
                  className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-zinc-800"
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pricing Options */}
        <div className="space-y-2">
          <div className="text-zinc-400">
            <span className="text-zinc-300">{t('products.subscriptionPricingCard.monthlyLabel')}</span>{' '}
            <span className="text-white font-medium">
              {t('products.subscriptionPricingCard.monthlyValue', {
                price: formatCurrency(plan.monthlyPrice, plan.currency),
              })}
            </span>
          </div>
          <div className="text-zinc-400">
            <span className="text-zinc-300">{t('products.subscriptionPricingCard.annualLabel')}</span>{' '}
            <span className="text-white font-medium">
              {t('products.subscriptionPricingCard.annualValue', {
                price: formatCurrency(plan.annualPrice, plan.currency),
              })}
            </span>{' '}
            <span className="text-zinc-500">
              {t('products.subscriptionPricingCard.annualSummary', {
                price: formatCurrency(plan.annualMonthlyPrice, plan.currency),
                discount: plan.annualDiscount,
              })}
            </span>
          </div>
          
          {/* Team-specific information */}
          {plan.type === 'teams' && plan.includedSeats && (
            <div className="pt-2 space-y-1">
              <div className="text-sm text-zinc-300">
                {t('products.subscriptionPricingCard.includedSeats', { count: plan.includedSeats })}
              </div>
              {plan.pricePerAdditionalSeat !== undefined && (
                <div className="text-sm text-zinc-400">
                  {t('products.subscriptionPricingCard.additionalSeat', {
                    price: formatCurrency(plan.pricePerAdditionalSeat, plan.currency),
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Features */}
        {plan.features.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium text-zinc-300">{t('products.subscriptionPricingCard.featuresTitle')}</div>
            <ul className="space-y-1.5">
              {plan.features.map((feature, index) => (
                <li key={index} className="text-sm text-zinc-400 flex items-start">
                  <span className="mr-2">•</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {plan.productId && plan.pricings && plan.pricings.length > 0 && (
          <div className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-900/60 p-3">
            <div className="text-xs uppercase tracking-wide text-zinc-400">
              {t('products.subscriptionPricingCard.share', 'Share checkout link')}
            </div>
            <div className="space-y-2">
              {plan.pricings.map((pricing, index) => (
                <div key={pricing.id ?? index} className="flex items-center justify-between gap-2 text-xs text-zinc-300">
                  <div className="space-y-1">
                    <div className="font-medium">{pricing.billingCycleName ?? pricing.billingCycleId ?? 'Pricing'}</div>
                    <div className="text-[11px] text-zinc-500">
                      {t('products.subscriptionPricingCard.pricingId', 'ID')}: {pricing.id ?? '—'} ·{' '}
                      {t('products.subscriptionPricingCard.cycleId', 'Cycle')}: {pricing.billingCycleId ?? '—'}
                    </div>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-zinc-300 hover:text-white hover:bg-zinc-800"
                    onClick={() => handleCopyLink(pricing.id, pricing.billingCycleId)}
                    title={t('products.subscriptionPricingCard.copyLink', 'Copy checkout link')}
                  >
                    {copiedId === pricing.id ? (
                      <ClipboardCheck className="h-4 w-4" />
                    ) : (
                      <LinkIcon className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
